import { Button } from "../../components/Button";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";

export type PaymentState = "success" | "pending" | "failed";

interface PaymentStatusScreenProps {
  state: PaymentState;
  /** Success only — when access runs to. */
  validUntil?: string | undefined;
  onContinue?: (() => void) | undefined;
  onRetry?: (() => void) | undefined;
  onAskParent?: (() => void) | undefined;
  onSupport?: (() => void) | undefined;
  onReportNotUnlocked?: (() => void) | undefined;
  /**
   * Pending only — ask the gateway again.
   *
   * The primary action on this screen, because the student has already paid.
   * What they want is for us to look again, not to be sent back to pay.
   */
  onCheckAgain?: (() => void) | undefined;
  checking?: boolean | undefined;
  /** Shown after a check that found nothing new. */
  checkMessage?: string | null | undefined;
}

const ICONS: Record<PaymentState, { glyph: string; className: string }> = {
  success: { glyph: "✓", className: "bg-[var(--positive-tint)] text-positive" },
  pending: { glyph: "⏱", className: "bg-surface-high text-text-muted" },
  failed: { glyph: "!", className: "bg-[rgba(228,87,79,0.14)] text-negative" },
};

/**
 * Screens 14 / 14b / 14c — one component, three states.
 *
 * The pending case exists because Paystack's webhook can arrive after the
 * student is back in the app: they have paid and nothing has changed, which
 * without this screen reads as "it failed, pay again". So it says the two
 * things that stop a double payment — you won't be charged twice, and you can
 * keep practising — rather than leaving them on a spinner.
 *
 * The failed case leads with "you weren't charged", because that is the only
 * question the student actually has.
 */
export function PaymentStatusScreen({
  state,
  validUntil,
  onCheckAgain,
  checking = false,
  checkMessage = null,
  onContinue,
  onRetry,
  onAskParent,
  onSupport,
  onReportNotUnlocked,
}: PaymentStatusScreenProps) {
  const icon = ICONS[state];

  return (
    <div className="app-column items-center px-5 pb-6 pt-10 text-center">
      <span
        aria-hidden="true"
        className={`grid h-16 w-16 place-items-center rounded-full text-[28px] ${icon.className}`}
      >
        {icon.glyph}
      </span>

      {state === "success" ? (
        <>
          <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.2]">
            You're in — everything's unlocked.
          </h1>
          <p className="tabular m-0 pt-2 text-[13.5px] text-text-muted">
            Paid {naira(ALL_ACCESS_MINOR)}
            {validUntil ? ` · valid till ${validUntil}` : ""}.
          </p>
          <ul className="m-0 flex w-full list-none flex-col gap-2.5 p-0 pt-6 text-left">
            {[
              "All subjects, unlimited practice",
              "Tutor and weekly mocks switched on",
              "Receipt sent by SMS",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[13.5px]">
                <span aria-hidden="true" className="text-positive">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-auto w-full pt-8">
            <Button onClick={onContinue}>Keep practicing</Button>
          </div>
        </>
      ) : state === "pending" ? (
        <>
          <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.2]">
            Hold on — confirming with your bank.
          </h1>
          <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
            Bank transfers can take a minute or two. You don't need to do anything — we'll unlock
            everything the moment it lands.
          </p>
          <ul className="m-0 flex w-full list-none flex-col gap-2.5 p-0 pt-6 text-left">
            {[
              "You won't be charged twice",
              "We'll SMS you when it's confirmed",
              "Your score and streak are safe",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[13.5px]">
                <span aria-hidden="true" className="text-text-muted">
                  ·
                </span>
                {item}
              </li>
            ))}
          </ul>
          {checkMessage ? (
            <p
              role="status"
              className="m-0 mt-5 w-full rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-[12.5px] leading-[1.5] text-text-muted"
            >
              {checkMessage}
            </p>
          ) : null}

          <div className="mt-auto flex w-full flex-col gap-3 pt-8">
            {/*
              Checking again is the primary action, not paying again. The
              student has already paid; "I paid but nothing changed" used to
              take them to the failed screen, which says the payment did not go
              through and offers to charge them a second time.
            */}
            {onCheckAgain ? (
              <Button disabled={checking} onClick={onCheckAgain}>
                {checking ? "Checking…" : "Check again"}
              </Button>
            ) : null}
            <Button variant={onCheckAgain ? "secondary" : "primary"} onClick={onContinue}>
              Keep practicing meanwhile
            </Button>
            <Button variant="ghost" onClick={onReportNotUnlocked}>
              Still nothing? Message support
            </Button>
          </div>
        </>
      ) : (
        <>
          <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.2]">
            That payment didn't go through.
          </h1>
          <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
            You weren't charged. If money left your account, your bank returns it automatically —
            usually within a day.
          </p>
          <div className="mt-6 w-full rounded-[var(--radius-card)] border border-border bg-surface p-4 text-left">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              Things that usually work
            </p>
            <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-2.5">
              <li className="text-[13px] leading-[1.45]">
                Try bank transfer or USSD instead of card
              </li>
              <li className="tabular text-[13px] leading-[1.45]">
                Check the account has up to {naira(ALL_ACCESS_MINOR)}
              </li>
            </ul>
          </div>
          <div className="mt-auto flex w-full flex-col gap-3 pt-8">
            <Button onClick={onRetry}>Try again</Button>
            <Button variant="info" onClick={onAskParent}>
              Ask a parent instead
            </Button>
            <button
              type="button"
              onClick={onSupport}
              className="m-0 text-[12.5px] text-text-muted underline"
            >
              Still stuck? Message support on WhatsApp
            </button>
          </div>
        </>
      )}
    </div>
  );
}
